/**
 * WebSocket Context
 * Provides team-level WebSocket connection across the application
 *
 * Architecture: Slack-style broadcast model
 * - All subscribers receive all messages
 * - Subscribers filter messages client-side (by threadId, messageType, etc.)
 * - Single WebSocket connection per team
 */

import { createContext } from 'preact';
import { useContext, useMemo, useCallback, useRef } from 'preact/hooks';
import { useWebSocket, type WebSocketMessage } from '../hooks/useWebSocket';
import { useAuth } from '../hooks/useAuth';
import { useScopeContext } from './ScopeContext';
import type { ComponentChildren } from 'preact';

interface WebSocketContextValue {
  connected: boolean;
  send: (message: WebSocketMessage) => void;
  lastMessage: WebSocketMessage | null;
  subscribe: (handler: (message: WebSocketMessage) => void) => () => void;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

/**
 * WebSocketProvider manages the team-level WebSocket connection
 *
 * Must be used within AuthProvider and ScopeProvider.
 * Automatically connects when user, token, and teamId are available.
 */
export function WebSocketProvider({ children }: { children: ComponentChildren }) {
  const { user, token } = useAuth();
  const scope = useScopeContext();
  const subscribersRef = useRef<Set<(message: WebSocketMessage) => void>>(new Set());

  const handleMessage = useCallback((message: WebSocketMessage) => {
    console.log('[WebSocketContext] Message received:', message.messageType, message.payload);

    // Broadcast to ALL subscribers (Slack model - they filter client-side)
    subscribersRef.current.forEach(handler => {
      handler(message);
    });
  }, []);

  const { connected, send, lastMessage } = useWebSocket({
    userId: user?.sub || user?.id || '',
    teamId: scope.teamId || '',
    token: token || '',
    onMessage: handleMessage,
    autoConnect: !!user && !!scope.teamId && !!token,
  });

  const subscribe = useCallback((handler: (message: WebSocketMessage) => void) => {
    console.log('[WebSocketContext] New subscriber added');
    subscribersRef.current.add(handler);

    // Return unsubscribe function
    return () => {
      console.log('[WebSocketContext] Subscriber removed');
      subscribersRef.current.delete(handler);
    };
  }, []);

  const value = useMemo(
    () => ({
      connected,
      send,
      lastMessage,
      subscribe,
    }),
    [connected, send, lastMessage, subscribe]
  );

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
}

/**
 * Hook to access WebSocket context
 * Must be used within WebSocketProvider
 */
export function useWebSocketContext(): WebSocketContextValue {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within WebSocketProvider');
  }
  return context;
}
